import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Sparkles, Zap, Crown, Building2, Star, Shield } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import SiteFooter from '../components/SiteFooter';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    tagline: 'Try before you buy',
    icon: Sparkles,
    accent: '#00F0FF',
    features: [
      '10 AI credits per month',
      'All 6 free lead-gen tools',
      'Community support',
      'PDF downloads',
      'Watermarked exports',
    ],
    cta: 'Start free',
    testid: 'plan-free',
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 19,
    priceLabel: '/month',
    tagline: 'For weekend builders',
    icon: Zap,
    accent: '#00F0FF',
    featured: false,
    features: [
      '200 AI credits every month',
      'All free tools + unlimited use',
      'Community support',
      'All templates',
      'No watermarks',
      'Save & re-open past chats',
    ],
    cta: 'Choose Starter',
    testid: 'plan-starter',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 49,
    priceLabel: '/month',
    tagline: 'Most popular',
    icon: Crown,
    accent: '#FF007F',
    featured: true,
    features: [
      '700 AI credits every month',
      'All Starter features',
      'Priority support',
      'Advanced model access (Claude 4.5)',
      'Butler Mode + custom SOULs',
      'Faster generation',
    ],
    cta: 'Choose Pro',
    testid: 'plan-pro',
  },
  {
    id: 'business',
    name: 'Business',
    price: 149,
    priceLabel: '/month',
    tagline: 'For growing teams',
    icon: Building2,
    accent: '#A855F7',
    features: [
      '2,500 AI credits every month',
      'All Pro features',
      'Team seats (up to 5)',
      'Dedicated support',
      'Custom domains',
      'API access',
    ],
    cta: 'Choose Business',
    testid: 'plan-business',
  },
];

const FAQ = [
  {
    q: 'What is an AI credit?',
    a: 'One credit is one AI generation — a chat response, a template export, an image generation, or one code snippet. Complex tasks may use multiple credits.',
  },
  {
    q: 'Can I change plans anytime?',
    a: 'Yes. Upgrade instantly and pay the difference pro-rata. Downgrade takes effect at the end of your billing period.',
  },
  {
    q: 'Do unused credits roll over?',
    a: 'Standard and Pro credits reset each month. Business credits roll over up to 2x monthly allowance.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — the Free plan is unlimited-duration. Paid plans have a 14-day money-back guarantee.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'All major credit/debit cards via Stripe. We also support Apple Pay, Google Pay, and (soon) crypto via Stripe Crypto.',
  },
  {
    q: 'Which AI models power the platform?',
    a: 'Claude Sonnet 4.5 (Anthropic), GPT-5 (OpenAI), Gemini 2.5 (Google). You can switch per request on Pro and Business.',
  },
];

export default function PricingPage() {
  const navigate = useNavigate();

  const handlePurchase = async (planId) => {
    if (planId === 'free') {
      navigate('/');
      return;
    }
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
        }),
      });
      if (!r.ok) throw new Error('Checkout failed');
      const data = await r.json();
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Could not start checkout. Please try again in a moment.');
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans relative overflow-hidden">
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00F0FF 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 -right-40 w-[720px] h-[720px] rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FF007F 0%, transparent 70%)' }}
      />

      {/* Header */}
      <header className="relative z-10 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              data-testid="pricing-back-btn"
              className="text-white/60 hover:text-white transition"
            >
              <ArrowLeft size={18} />
            </button>
            <BrandLogo size={32} />
          </div>
          <button
            onClick={() => navigate('/build')}
            data-testid="pricing-nav-build"
            className="rounded-full px-4 py-1.5 text-sm font-semibold text-black"
            style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
          >
            Launch builder
          </button>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-6 py-16">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] font-mono uppercase tracking-[0.25em] text-white/60 mb-6">
            <Star size={11} className="text-amber-300" /> Simple, transparent pricing
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-black tracking-tight leading-[0.95]">
            Ship faster.
            <br />
            <span
              style={{
                background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Pay less.
            </span>
          </h1>
          <p className="mt-6 text-lg text-white/60 leading-relaxed">
            Every plan includes access to Claude 4.5, GPT-5, and Gemini 2.5.
            <br />
            Cancel anytime. No lock-in. No surprise fees.
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            return (
              <div
                key={plan.id}
                data-testid={plan.testid}
                className={`relative rounded-3xl border p-6 flex flex-col transition ${
                  plan.featured
                    ? 'border-white/25 bg-white/[0.05] shadow-[0_25px_70px_-20px_rgba(255,0,127,0.4)]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                }`}
                style={
                  plan.featured
                    ? {
                        background:
                          'linear-gradient(180deg, rgba(0,240,255,0.06) 0%, rgba(255,0,127,0.06) 100%), rgba(255,255,255,0.02)',
                      }
                    : {}
                }
              >
                {plan.featured && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-black"
                    style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
                  >
                    ★ Most popular
                  </div>
                )}
                <div
                  className="h-10 w-10 rounded-xl inline-flex items-center justify-center"
                  style={{ background: `${plan.accent}22`, color: plan.accent }}
                >
                  <Icon size={18} />
                </div>
                <div className="mt-4">
                  <h3 className="text-xl font-display font-black tracking-tight">{plan.name}</h3>
                  <p className="text-[12px] text-white/50 mt-0.5">{plan.tagline}</p>
                </div>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-display font-black">${plan.price}</span>
                  {plan.priceLabel && (
                    <span className="text-white/50 text-sm">{plan.priceLabel}</span>
                  )}
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-white/80">
                      <Check
                        size={14}
                        style={{ color: plan.accent }}
                        className="mt-0.5 shrink-0"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handlePurchase(plan.id)}
                  data-testid={`${plan.testid}-cta`}
                  className={`mt-7 w-full h-11 rounded-xl text-sm font-semibold transition ${
                    plan.featured ? 'text-black' : 'text-white border border-white/15 hover:bg-white/[0.05]'
                  }`}
                  style={
                    plan.featured
                      ? { background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }
                      : {}
                  }
                >
                  {plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* Trust row */}
        <div className="mt-16 grid md:grid-cols-3 gap-4 text-center">
          {[
            { icon: Shield, title: 'Secure by default', desc: 'Stripe · TLS · SOC 2 in progress' },
            { icon: Sparkles, title: 'Latest AI models', desc: 'Claude 4.5, GPT-5, Gemini 2.5' },
            { icon: Zap, title: 'Cancel anytime', desc: '14-day money-back guarantee' },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <div
                key={t.title}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
              >
                <div
                  className="h-10 w-10 rounded-xl mx-auto flex items-center justify-center text-[#00F0FF]"
                  style={{ background: 'rgba(0,240,255,0.1)' }}
                >
                  <Icon size={18} />
                </div>
                <div className="mt-3 font-bold">{t.title}</div>
                <div className="text-[13px] text-white/55 mt-1">{t.desc}</div>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-2">
              FAQ
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-black tracking-tight">
              Questions &amp; answers
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="pricing-faq">
            {FAQ.map((item, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] transition"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-semibold text-white pr-4">{item.q}</span>
                  <span className="text-white/40 group-open:rotate-180 transition-transform text-lg leading-none">
                    ⌄
                  </span>
                </summary>
                <p className="mt-3 text-[13.5px] text-white/65 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-display font-black tracking-tight">
            Not sure which plan?
          </h3>
          <p className="mt-3 text-white/60 max-w-xl mx-auto">
            Chat with our team — we&rsquo;ll help you pick the plan that fits your goals in under
            10 minutes.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/contact')}
              data-testid="pricing-contact-cta"
              className="rounded-full px-5 py-2.5 text-sm font-semibold text-black"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              Talk to us
            </button>
            <button
              onClick={() => navigate('/build')}
              data-testid="pricing-try-cta"
              className="rounded-full border border-white/15 hover:bg-white/[0.05] px-5 py-2.5 text-sm text-white transition"
            >
              Try the builder →
            </button>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
